import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtUSD, fmtUSDT, fmtDate } from "../lib/format";
import {
  ArrowUpRight,
  ArrowDownLeft,
  TrendingUp,
  Clock,
  Calendar,
} from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const TYPE_LABEL = {
  chime: "Chime",
  cashapp: "CashApp",
  paypal: "PayPal",
  venmo: "Venmo",
  link: "Link",
};

function StatCard({ label, value, sub, accent, testId, onClick, clickable }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`stat-card p-5 text-left transition-shadow ${
        clickable
          ? "hover:shadow-md hover:border-zinc-300 cursor-pointer"
          : "cursor-default"
      }`}
      data-testid={testId}
    >
      <div className="text-[11px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div
          className={`font-mono text-[26px] font-medium tracking-tight ${
            accent || ""
          }`}
        >
          {value}
        </div>
      </div>
      {sub && <div className="text-[11.5px] text-zinc-500 mt-1">{sub}</div>}
      {clickable && (
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-2">
          Click for breakdown →
        </div>
      )}
    </button>
  );
}

const today = () => new Date().toISOString().slice(0, 10);
const yesterday = () => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

const PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "custom", label: "Custom" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [breakdown, setBreakdown] = useState(null);

  const range = useMemo(() => {
    if (preset === "today") return { from: today(), to: today() };
    if (preset === "yesterday")
      return { from: yesterday(), to: yesterday() };
    if (preset === "custom" && (from || to)) return { from, to };
    return {};
  }, [preset, from, to]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      const dr = await api.get("/dashboard", { params });
      setData(dr.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [range.from, range.to]);

  const filterLabel = (() => {
    if (preset === "all") return "All time";
    if (preset === "today") return `Today · ${range.from}`;
    if (preset === "yesterday") return `Yesterday · ${range.from}`;
    if (range.from && range.to) return `${range.from} → ${range.to}`;
    if (range.from) return `From ${range.from}`;
    if (range.to) return `Until ${range.to}`;
    return "Custom";
  })();

  return (
    <Layout
      title="Dashboard"
      subtitle="Real-time finance control room"
      testId="dashboard-page"
    >
      {/* Date filter bar */}
      <div className="stat-card p-3 mb-5 flex flex-wrap items-center gap-2">
        <Calendar size={13} strokeWidth={1.7} className="text-zinc-500 ml-1" />
        <span className="text-[11.5px] uppercase tracking-widest text-zinc-500 mr-1">
          Period
        </span>
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPreset(p.id)}
            data-testid={`dashboard-filter-${p.id}`}
            className={`h-8 px-3 rounded-md text-[12.5px] border transition-colors ${
              preset === p.id
                ? "bg-zinc-900 text-white border-zinc-900"
                : "hairline bg-white text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8 w-[150px]"
              data-testid="dashboard-filter-from"
            />
            <span className="text-zinc-400 text-xs">→</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-8 w-[150px]"
              data-testid="dashboard-filter-to"
            />
          </div>
        )}
        <span
          className="ml-auto text-[11.5px] text-zinc-500 font-mono"
          data-testid="dashboard-filter-label"
        >
          {filterLabel}
        </span>
      </div>

      {loading || !data ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              label="Total Balance"
              value={fmtUSD(data.total_balance_usd)}
              sub={`${data.wallet_count} wallets`}
              testId="stat-total-balance"
              clickable
              onClick={() =>
                setBreakdown({
                  title: "Total Balance · by provider",
                  desc: "Live wallet balances grouped by provider.",
                  testId: "breakdown-total-balance",
                  rows: Object.keys(TYPE_LABEL).map((t) => ({
                    label: TYPE_LABEL[t],
                    value: data.by_type[t] || 0,
                    sub: `${data.count_by_type[t] || 0} wallets`,
                  })),
                  total: data.total_balance_usd,
                })
              }
            />
            <StatCard
              label={`Total Withdrawal · ${
                preset === "all" ? "All time" : filterLabel
              }`}
              value={fmtUSD(data.total_withdrawal_usd)}
              sub={fmtUSDT(data.total_withdrawal_usdt)}
              testId="stat-total-withdrawal"
              clickable
              onClick={() =>
                setBreakdown({
                  title: `Total Withdrawal · ${
                    preset === "all" ? "All time" : filterLabel
                  }`,
                  desc: "Sum of withdrawal transactions per provider in this period.",
                  testId: "breakdown-total-withdrawal",
                  rows: Object.keys(TYPE_LABEL).map((t) => ({
                    label: TYPE_LABEL[t],
                    value: data.withdrawal_by_type?.[t] || 0,
                    sub: `${(
                      data.withdrawal_usdt_by_type?.[t] || 0
                    ).toLocaleString("en-US", {
                      maximumFractionDigits: 4,
                    })} USDT`,
                  })),
                  total: data.total_withdrawal_usd,
                })
              }
            />
            <StatCard
              label="Opening Balance · Today"
              value={fmtUSD(data.opening_balance_day_usd)}
              sub="All wallets at 00:00 UTC"
              testId="stat-opening-day"
              clickable
              onClick={() =>
                setBreakdown({
                  title: "Opening Balance · Today",
                  desc: "Per-provider balance at start of day (current minus today's manual updates).",
                  testId: "breakdown-opening-day",
                  rows: Object.keys(TYPE_LABEL).map((t) => ({
                    label: TYPE_LABEL[t],
                    value: data.opening_day_by_type?.[t] || 0,
                    sub: `${data.count_by_type[t] || 0} wallets`,
                  })),
                  total: data.opening_balance_day_usd,
                })
              }
            />
            <StatCard
              label="Opening Balance · Month"
              value={fmtUSD(data.opening_balance_month_usd)}
              sub="First day of the month"
              testId="stat-opening-month"
              clickable
              onClick={() =>
                setBreakdown({
                  title: "Opening Balance · Month",
                  desc: "Per-provider balance at start of month (current minus month's manual updates).",
                  testId: "breakdown-opening-month",
                  rows: Object.keys(TYPE_LABEL).map((t) => ({
                    label: TYPE_LABEL[t],
                    value: data.opening_month_by_type?.[t] || 0,
                    sub: `${data.count_by_type[t] || 0} wallets`,
                  })),
                  total: data.opening_balance_month_usd,
                })
              }
            />
          </div>

          <div className="mt-8 stat-card p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-bold tracking-tight">
                  Balance by Provider
                </div>
                <div className="text-[11.5px] text-zinc-500 mt-0.5 flex items-center gap-1.5">
                  <Clock size={11} strokeWidth={1.6} />
                  Last updated {fmtDate(data.last_updated)}
                </div>
              </div>
              <TrendingUp
                size={15}
                strokeWidth={1.5}
                className="text-zinc-400"
              />
            </div>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.keys(TYPE_LABEL).map((t) => {
                const total = data.total_balance_usd || 1;
                const val = data.by_type[t] || 0;
                const pct = Math.max(0, Math.min(100, (val / total) * 100));
                return (
                  <div
                    key={t}
                    className="rounded-md border hairline p-3.5"
                    data-testid={`by-type-${t}`}
                  >
                    <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
                      {TYPE_LABEL[t]}
                    </div>
                    <div className="font-mono text-[17px] font-medium mt-1">
                      {fmtUSD(val)}
                    </div>
                    <div className="mt-2 h-1 bg-zinc-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-zinc-900"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="text-[10.5px] text-zinc-500 mt-1.5">
                      {data.count_by_type[t] || 0} wallets
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-8 stat-card p-5">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold tracking-tight">
                Recent Transactions
              </div>
              <span className="text-[11.5px] text-zinc-500">
                {filterLabel} · last 10
              </span>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline">
                    <th className="py-2 pr-4 font-medium">When</th>
                    <th className="py-2 pr-4 font-medium">Wallet</th>
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 font-medium">Kind</th>
                    <th className="py-2 pr-4 font-medium text-right">
                      Amount (USD)
                    </th>
                    <th className="py-2 pr-4 font-medium text-right">USDT</th>
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {data.recent_transactions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-zinc-500 text-center">
                        No transactions in this period
                      </td>
                    </tr>
                  ) : (
                    data.recent_transactions.map((t) => (
                      <tr
                        key={t.id}
                        className="row-hover"
                        data-testid={`recent-txn-${t.id}`}
                      >
                        <td className="py-2.5 pr-4 font-mono text-[12px] text-zinc-600">
                          {fmtDate(t.created_at)}
                        </td>
                        <td className="py-2.5 pr-4">{t.wallet_name}</td>
                        <td className="py-2.5 pr-4 text-zinc-600">
                          {TYPE_LABEL[t.wallet_type] || t.wallet_type}
                        </td>
                        <td className="py-2.5 pr-4">
                          <span
                            className={`inline-flex items-center gap-1 text-[11.5px] px-1.5 py-0.5 rounded border ${
                              t.kind === "deposit"
                                ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                                : t.kind === "withdrawal"
                                ? "text-rose-700 border-rose-200 bg-rose-50"
                                : "text-sky-700 border-sky-200 bg-sky-50"
                            }`}
                          >
                            {t.kind === "deposit" ? (
                              <ArrowDownLeft size={11} strokeWidth={1.8} />
                            ) : t.kind === "withdrawal" ? (
                              <ArrowUpRight size={11} strokeWidth={1.8} />
                            ) : null}
                            {t.kind}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 font-mono text-right">
                          {fmtUSD(t.amount_usd)}
                        </td>
                        <td className="py-2.5 pr-4 font-mono text-right">
                          {t.amount_usdt != null
                            ? t.amount_usdt.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })
                            : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Dialog
        open={!!breakdown}
        onOpenChange={(o) => !o && setBreakdown(null)}
      >
        <DialogContent
          data-testid={breakdown?.testId || "breakdown-dialog"}
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {breakdown?.title}
            </DialogTitle>
            <DialogDescription>{breakdown?.desc}</DialogDescription>
          </DialogHeader>
          <div className="divide-y hairline">
            {breakdown?.rows?.map((r) => {
              const total = breakdown.total || 0;
              const pct =
                total !== 0 ? Math.abs((r.value / total) * 100) : 0;
              return (
                <div
                  key={r.label}
                  className="py-2.5 flex items-center justify-between gap-4"
                  data-testid={`breakdown-row-${r.label.toLowerCase()}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium">{r.label}</div>
                    <div className="text-[10.5px] text-zinc-500 mt-0.5">
                      {r.sub}
                    </div>
                    <div className="mt-1.5 h-1 bg-zinc-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-zinc-900"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>
                  <div className="font-mono text-[14px] font-medium tabular-nums whitespace-nowrap">
                    {fmtUSD(r.value)}
                  </div>
                </div>
              );
            })}
            <div className="py-2.5 flex items-center justify-between bg-zinc-50/60 -mx-6 px-6 rounded-b">
              <div className="text-[12px] uppercase tracking-widest text-zinc-500">
                Total
              </div>
              <div className="font-mono text-[15px] font-semibold tabular-nums">
                {fmtUSD(breakdown?.total || 0)}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
